<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router'
import type { ItemLink } from '~/types/elementsUi'
import { logoImages } from '~/models/images'

const urlHistoryStore = useUrlHistoryStore()
const { isConsentGiven } = useCookieControl()

const { t, tm } = useI18n('layouts.default.footer')

const footerSectionTwo = computed<ItemLink[]>(() => tm('.section-two'))
const footerSectionThree = computed<ItemLink[]>(() => tm('.section-three'))

const eventTracking = async (action: string) => {
  if (action === 'Aanmelden voor een maaltijd' || action === 'Aanmelden als thuiskok') {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
      event: 'clickEvents',
      params: {
        category: 'Klik in footer menu',
        action: action,
        label: ''
      }
    })
  }

  scrollTo(0, 0)
}

const openCookieSettings = async () => {
  urlHistoryStore.hasMadeCookieDecision = false
  await nextTick()
  isConsentGiven.value = undefined
}
</script>

<template>
  <footer class="bg-beige-700 dark:bg-pure-black-700">
    <section class="section">
      <div class="mb-8">
        <FooterLogo />
      </div>

      <p class="section-title text-left text-h3 text-black dark:text-white">
        {{ t('.prefer-to-call') }}
      </p>

      <div class="mb-8 items-center justify-between md:flex">
        <div class="items-center md:flex">
          <p>
            {{ t('.opening-hours.opening') }}
            <br />
            <span class="font-bold">{{ t('.opening-hours.days') }}</span>
            <br />
            <span class="font-bold">{{ t('.opening-hours.hours') }}</span>
            <br />
            {{ t('.opening-hours.close') }}
          </p>
          <p class="max-md:mt-2 max-md:mb-8 md:ml-16">
            {{ t('.call-us.title') }}
            <br />
            <VLink
              label=".call-us.phone"
              class="font-bold text-default"
              href="tel:085-0608768"
            />
          </p>
        </div>

        <SocialMediaIcons />
      </div>

      <div class="block">
        <ul class="mt-5">
          <li
            v-for="(option, index) in footerSectionTwo"
            :key="`footer-item-${index}`"
            class="font-semibold"
          >
            <VLink
              :to="{ name: option.link_DNT } as RouteLocationRaw"
              class="flex cursor-pointer items-center justify-between no-underline"
              @click="eventTracking(option.item)"
            >
              <p class="my-0! paragraph-title max-w-2/3 font-semibold">
                {{ option.item }}
              </p>
              <VIcon
                icon="fa-chevron-right"
                dark-color="success"
                :ui="{
                  icon: 'text-3xl'
                }"
              />
            </VLink>
            <VDivider class="mt-4 mb-6 h-px border-black dark:border-gray-700" />
          </li>
        </ul>
      </div>
    </section>

    <section class="section">
      <div class="columns multiline">
        <div
          v-for="(option, index) in footerSectionThree"
          :key="`${option.link_DNT}-${index}`"
          class="column one-quarter"
        >
          <VLink
            :label="option.item"
            :to="{ name: option.link_DNT } as RouteLocationRaw"
            class="text-default"
          />
        </div>
      </div>
    </section>

    <section class="section pt-0">
      <div class="flex items-center justify-center">
        <ANBILogo />
        <VImage :image="logoImages.cfb" sizes="225px" width="225px" height="125px" />
      </div>

      <ul class="flex flex-col justify-center lg:flex-row">
        <li class="text-xl font-light lg:text-xs">
          <VLink
            label=".our-terms"
            :to="{ name: 'algemene-voorwaarden' }"
            class="text-default"
          />
        </li>

        <li class="text-xl font-light lg:text-xs">
          <span class="lg:before:mx-[0.4rem]! lg:before:my-0! lg:before:content-['|']" />
          <VLink
            label=".privacy-policy"
            :to="{ name: 'privacy-statement' }"
            class="text-default"
          />
        </li>

        <li class="text-xl font-light lg:text-xs">
          <span class="lg:before:mx-[0.4rem]! lg:before:my-0! lg:before:content-['|']" />
          <VHtmlButton class="text-default underline" @click="openCookieSettings">{{
            t('.cookie-settings')
          }}</VHtmlButton>
        </li>

        <li class="text-xl font-light lg:text-xs">
          <span class="lg:before:mx-[0.4rem]! lg:before:my-0! lg:before:content-['|']" />
          <VLink
            label=".sitemap"
            href="https://thuisgekookt.nl/sitemap.xml"
            class="text-default"
          />
        </li>
      </ul>
    </section>
  </footer>
</template>

<style scoped></style>
